import has from 'lodash/has';
import { call, put, select } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import * as types from '../../constants/ActionTypes';
import Api from '../../functions/Api';
import providers from '../../functions/payment';
import { PAYMENT_PROVIDER } from '../../constants/branding';
import { triggerUpdateQuestData } from '../quests';

export function* checkout(action) {
  let { data } = action;
  const { additional } = action;

  if (!has(providers, PAYMENT_PROVIDER)) {
    yield put({
      type: types.PAYMENT_FAILURE,
      error: 'No such provider',
    });
  }

  try {
    const locale = yield select((state) => state.user.locale);
    if (PAYMENT_PROVIDER === 'yandex') {
      data = {
        ...data,
        cps_email: data.cpsEmail,
        ym_merchant_receipt: data.ymMerchantReceipt,
      };
    }
    yield call(providers[PAYMENT_PROVIDER].checkout, data, additional, locale);
  } catch (error) {
    yield put({
      type: types.PAYMENT_FAILURE,
      error,
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

export function* publishProduct({ idProduct }) {
  try {
    const response = yield call(Api.post, `v2/products/${idProduct}/publish/`);

    if (response) {
      yield put({
        type: types.PUBLISH_PRODUCT_SUCCESS,
      });
      const currentQuestId = yield select(
        (state) => state.quests.currentQuestId
      );
      yield put(push(`/quest/${currentQuestId}/publish`));
      yield put({
        type: types.SYNC_QUEST_STAT,
        questId: currentQuestId,
        status: 'waiting_for_moderation',
      });
    }
  } catch (error) {
    yield put({
      type: types.PUBLISH_PRODUCT_FAILURE,
      errors: [error],
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

export function* sendPublishData(action) {
  try {
    const { data } = yield call(
      Api.put,
      `v3/products/${action.id}/?expand=attractions,purchases_count&currency=DEF`,
      {
        data: {
          id: action.id,
          ...action.data,
        },
        isDecamelize: false,
      }
    );

    if (data && data.id) {
      const questId = yield select((state) => state.quests.currentQuestId);
      const quests = yield select((state) => state.quests.quests);

      yield put({
        type: types.SEND_PUBLISH_DATA_SUCCESS,
        data,
      });
      const { generatedByAi, originalTour } = quests[0] || {};
      if (generatedByAi === true && originalTour === null) {
        yield put(triggerUpdateQuestData(questId));
      }

      const dataForListing = {
        title: data.title,
        eventsCount: data.tour.eventsCount,
        lastModified: data.lastModified,
        reviewsCount: data.reviewsCount,
        purchasesCount: data.purchasesCount,
        status: data.status,
      };

      yield put({
        type: types.UPDATE_QUEST_STAT,
        questId: Number(questId),
        data: dataForListing,
      });
      yield put({
        type: types.FETCH_QUEST,
        id: questId,
        isForSync: true,
      });
    }
  } catch (error) {
    yield put({
      type: types.SEND_PUBLISH_DATA_FAILURE,
      errors: [error],
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

export function* patchProductV1(action) {
  try {
    const response = yield call(Api.patch, `v1/products/${action.id}/`, {
      data: action.data,
      isDecamelize: false,
    });
    const data = response?.data ?? response ?? {};

    const prevPublishData = yield select(
      (state) => state.publishing.receivedPublishData
    );
    const languages = yield select((state) => state.languages.items || []);
    const currencies = yield select((state) => state.currencies.items || []);
    const localeId =
      data.localeId ??
      data.locale_id ??
      data.localId ??
      data.local_id ??
      action.data?.locale_id ??
      action.data?.localeId ??
      action.data?.local_id ??
      prevPublishData.localeId ??
      null;
    const localeFromLocaleId = localeId
      ? languages.find((lang) => Number(lang.id) === Number(localeId))?.code
      : null;
    const nextCurrencyId =
      data.defaultCurrency?.id ??
      data.default_currency?.id ??
      data.currencyId ??
      data.currency_id ??
      action.data?.currency_id ??
      action.data?.currencyId ??
      prevPublishData.defaultCurrency?.id ??
      null;
    const defaultCurrencyFromId = nextCurrencyId
      ? currencies.find((c) => Number(c.id) === Number(nextCurrencyId))
      : null;
    const nextDefaultCurrency =
      data.defaultCurrency ||
      data.default_currency ||
      defaultCurrencyFromId ||
      prevPublishData.defaultCurrency;

    const mapped = {
      ...prevPublishData,
      ...data,
      id: data.id ?? prevPublishData.id ?? action.id,
      localeId: data.localeId ?? data.locale_id ?? localeId ?? null,
      locale: data.locale ?? localeFromLocaleId ?? prevPublishData.locale ?? '',
      defaultCurrency: nextDefaultCurrency,
      // AICODE-NOTE: meeting_point/end_point are always present in a v1 Update response (an object
      // or null, never absent) and, when present, are the full current point - not a partial patch
      // echo - so they're authoritative and must NOT fall back to prevPublishData, or clearing a
      // point via "end_point: null" would silently keep showing the stale address/title/location.
      address:
        'meeting_point' in data
          ? data.meeting_point?.address ?? ''
          : data.address ?? prevPublishData.address,
      startLocation:
        'meeting_point' in data
          ? data.meeting_point?.title ?? ''
          : data.startLocation ?? prevPublishData.startLocation,
      locationGeo:
        'meeting_point' in data
          ? data.meeting_point?.location ?? null
          : data.locationGeo ?? prevPublishData.locationGeo,
      finishAddress:
        'end_point' in data
          ? data.end_point?.address ?? ''
          : data.finishAddress ?? prevPublishData.finishAddress,
      finishLocation:
        'end_point' in data
          ? data.end_point?.title ?? ''
          : data.finishLocation ?? prevPublishData.finishLocation,
      finishLocationGeo:
        'end_point' in data
          ? data.end_point?.location ?? null
          : data.finishLocationGeo ?? prevPublishData.finishLocationGeo,
      distance: data.distance ?? prevPublishData.distance ?? null,
      durationMin:
        data.duration?.min ?? data.durationMin ?? prevPublishData.durationMin,
      durationMax:
        data.duration?.max ?? data.durationMax ?? prevPublishData.durationMax,
    };
    yield put({
      type: types.SEND_PUBLISH_DATA_SUCCESS,
      data: mapped,
    });
  } catch (error) {
    yield put({
      type: types.SEND_PUBLISH_DATA_FAILURE,
      errors: [error],
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

export function* fetchProduct(action) {
  try {
    const { data } = yield call(
      Api.get,
      `v3/products/${action.id}/?expand=inclusions,exclusions,highlights,price,images,attractions&currency=DEF`
    );

    yield put({
      type: types.RECEIVE_PRODUCT,
      data,
    });
  } catch (error) {
    yield put({
      type: types.NOT_RECEIVE_PRODUCT,
      errors: [error],
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}
